import { AttachFileOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { Controller } from "react-hook-form";

import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import Button from "../../../components/Buttons/Button/Button";
import FileSelector from "../../../components/FileSelector/FileSelector";
import ConfirmationDialog from "../../../components/Modal/ConfirmationDialog";
import Progress from "../../../components/Progress/ProgressModal/ProgressModal";
import SingleSelect from "../../../components/SingleSelect/SingleSelect";
import { AppContext } from "../../../core/context/appContextProvider";
import { ListContainer } from "../../clientDetails/ReportConfig/SummaryConfiguration/SummaryConfiguration.style";
import { ButtonBox } from "../fundList/FundList.styles";
import { useJournalEntries } from "./JournalEntries.hooks";
import { DatePicker, HeaderRow } from "./JournalEntries.style";

type Transaction = {
  id: string | null;
  index: number;
  account: string;
  debit: string;
  credit: string;
  description: string;
  name: string;
};

type JournalEntryProp = {
  fundId?: string;
  currency: string;
  closeDrawer: Function;
};

export const JournalEntries = ({
  fundId,
  currency,
  closeDrawer,
}: JournalEntryProp) => {
  const { informationAlert } = useContext(AppContext);

  const {
    glAccounts,
    formValues,
    performDelete,
    fields,
    control,
    totalCredit,
    totalDebit,
    handleAddTxLine,
    handleCancel,
    handleDelete,
    handleDeleteAll,
    handleSumbitJournal,
    handleSubmit,
    popupDeleteConfirmationBox,
  } = useJournalEntries({
    fundId,
    currency,
    closeDrawer,
    informationAlert,
  });

  return (
    <form onSubmit={handleSubmit(handleSumbitJournal)}>
      <Box id="add_journal_ledger_list" marginLeft={4}>
        <Progress id="journal_ledger_list_loading" showProgress={false} />
        <HeaderRow>
          <Typography variant="h2">Journal Entries</Typography>
        </HeaderRow>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              id="journal_number"
              label="Jorunal No."
              size="small"
              variant="outlined"
              value={1}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Controller
              name="date"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <DatePicker
                  label="Journal Date"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      id="journal_entry_date"
                    />
                  )}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  value={value}
                />
              )}
              rules={{
                required: "Journal date is required.",
              }}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Controller
              name="adjustingEntry"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl>
                  <FormControlLabel
                    id="adjusting_entry"
                    control={
                      <Checkbox
                        checked={value}
                        name="Adjustable"
                        onChange={onChange}
                      />
                    }
                    label="Is Adjusting Journal Entry?"
                  />
                </FormControl>
              )}
              control={control}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <Box>
              <HorizontalBox>
                <ButtonBox>
                  <Button
                    id="btn_add_new_transaction"
                    color="primary"
                    onClick={handleAddTxLine}
                    icon={<AddIcon />}
                    text="Add New Line"
                  />
                  <Button
                    id="btn_remove_all_transactions"
                    color="error"
                    onClick={popupDeleteConfirmationBox}
                    icon={<TrashIcon />}
                    text="Remove All Lines"
                  />
                </ButtonBox>
              </HorizontalBox>
              <ListContainer p={1} sx={{ borderBottom: "1px solid grey" }}>
                <List
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  {fields.map((item: any, index: number) => {
                    return (
                      <Stack
                        key={index}
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 1, md: 2 }}
                        mb={2}
                        alignItems="center"
                      >
                        <Typography>{index + 1}</Typography>
                        <Controller
                          name={`transactions.${index}.account`}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <SingleSelect
                              id={`account_${index}`}
                              optionList={glAccounts.map((item: any) => ({
                                label: item.name,
                                value: item.id,
                              }))}
                              label="Account"
                              idField="value"
                              labelField="label"
                              handleOptionSelection={onChange}
                              value={value ?? ""}
                              error={!!error}
                              noGutter={true}
                              width="auto"
                              helperText={error?.message ?? ""}
                            />
                          )}
                          control={control}
                        />
                        <Controller
                          name={`transactions.${index}.debit`}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <TextField
                              id={`debit_${index}`}
                              label="Debit"
                              size="small"
                              disabled={formValues[index]?.credit?.length > 0}
                              variant="outlined"
                              value={value}
                              onChange={onChange}
                              error={!!error}
                            />
                          )}
                          rules={{
                            min: 0,
                          }}
                          control={control}
                        />
                        <Controller
                          name={`transactions.${index}.credit`}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <TextField
                              id={`credit_${index}`}
                              label="Credit"
                              size="small"
                              variant="outlined"
                              value={value}
                              disabled={formValues[index]?.debit?.length > 0}
                              onChange={onChange}
                              error={!!error}
                            />
                          )}
                          rules={{
                            min: 0,
                          }}
                          control={control}
                        />
                        <Controller
                          name={`transactions.${index}.description`}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <TextField
                              id={`description_${index}`}
                              label="Description"
                              size="small"
                              variant="outlined"
                              value={value}
                              onChange={onChange}
                              error={!!error}
                              helperText={error?.message || ""}
                            />
                          )}
                          rules={{
                            maxLength: 255,
                            minLength: 0,
                          }}
                          control={control}
                        />
                        <Controller
                          name={`transactions.${index}.name`}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <TextField
                              id={`name_${index}`}
                              label="Name"
                              size="small"
                              variant="outlined"
                              value={value}
                              onChange={onChange}
                              error={!!error}
                              helperText={error?.message || ""}
                            />
                          )}
                          rules={{
                            required: "Name is required.",
                          }}
                          control={control}
                        />
                        <IconButton
                          onClick={() => handleDelete(item.id, index)}
                        >
                          <TrashIcon color="error" />
                        </IconButton>
                      </Stack>
                    );
                  })}
                  <Stack
                    direction={{ md: "row" }}
                    spacing={{ md: 2 }}
                    mb={2}
                    alignItems="center"
                  >
                    <Typography sx={{ opacity: 0 }}>0</Typography>
                    <div style={{ opacity: 0 }}>
                      <SingleSelect
                        id="account_selection_box"
                        optionList={[]}
                        label="Account"
                        handleOptionSelection={() => {}}
                        value={""}
                        noGutter={true}
                        width="auto"
                      />
                    </div>
                    <TextField
                      id="total_debit"
                      label="Total Debit"
                      size="small"
                      variant="outlined"
                      disabled={true}
                      value={totalDebit}
                    />
                    <TextField
                      id="total_credit"
                      label="Total Credit"
                      size="small"
                      variant="outlined"
                      disabled={true}
                      value={totalCredit}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      sx={{ opacity: 0 }}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      sx={{ opacity: 0 }}
                    />
                    <IconButton sx={{ opacity: 0 }}>
                      <TrashIcon />
                    </IconButton>
                  </Stack>
                </List>
              </ListContainer>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Controller
              name="memo"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  id="memo"
                  label="Memo"
                  rows={4}
                  multiline
                  fullWidth
                  value={value}
                  onChange={onChange}
                  error={!!error}
                />
              )}
              control={control}
            />
          </Grid>
          <Box width="100%" />
          <Grid item xs={12} md={6} lg={4}>
            <Stack>
              <Stack direction="row" alignItems="center">
                <AttachFileOutlined color="primary" />
                <Typography>Attachments, Max. 20MB</Typography>
              </Stack>
              <FileSelector
                title="Drag/Drop files here or click to upload."
                onUpload={() => {}}
                multiple={true}
              />
            </Stack>
          </Grid>
        </Grid>
        <ConfirmationDialog
          open={performDelete}
          onClose={handleCancel}
          id="delete_lines_confirmation"
          actions={[
            {
              label: "Delete Permanently",
              onClick: () => handleDeleteAll(),
              id: "btn_delete_transaction",
              variant: "contained",
              color: "primary",
            },
            {
              label: "Cancel",
              onClick: handleCancel,
              id: "btn_cancel_delete",
              variant: "outlined",
              color: "error",
            },
          ]}
          content="Are you sure you want to remove all the lines? This action cannot be undone."
          title="Delete All Lines?"
        />
      </Box>
      <HorizontalBox
        addTopShadow
        attachToBottom={true}
        hidden={false}
        fullWidth
      >
        <Button
          id={"investment_transaction_cancel_button"}
          variant="outlined"
          onClick={closeDrawer}
          text={"Cancel"}
          color={"secondary"}
          fullWidth
          addSpaceBetweenButtons
        />
        <Button
          id={"investment_transaction_save_button"}
          variant="contained"
          text={"Save & Close"}
          color={"primary"}
          type="submit"
          fullWidth
          addSpaceBetweenButtons
        />
      </HorizontalBox>
    </form>
  );
};
